import { loadSponsors } from "../../data-functions/system-data_api";
import { useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../../components/auth-provider";
import { SponsorsDict } from "../../types/sponsors";
import { SeasonsDict } from "../../types/orders";
import { AgentsDict } from "../../types/agents";
import { loadAgents } from "../../data-functions/agent-api";
import { loadSeasons, loadWorkOrders } from "../../data-functions/orders-api";
import { Box, IconButton, MenuItem, TableCell, Typography } from "@mui/material";
import { Tooltip } from "@mui/material";
import { TextField } from "@mui/material";
import { TitledPage } from "../../components/titled-page";
import { Save as SaveIcon, Refresh, Group } from "@mui/icons-material";
import { HeadCells, Order } from "../../types/tables";
import { APPLICATION_STATUS } from "../../types/visa-application";
import { ApplicationStats } from "../../types/stats";
import { loadApplicationStats } from "../../data-functions/stats-api";
import { TableVirtuoso } from "react-virtuoso";
import { VirtuosoTableComponents } from "../../components/enhanced-virtuoso-table";
import { EnhancedTableHeadContents } from "../../components/enhanced-table";
import { Filter } from "../../types/commons";
import { loadApplications } from "../../data-functions/applications-api";

export default function PageApplicationStats() {
  const [sponsors, setSponsors] = useState<SponsorsDict>({});
  const [agents, setAgents] = useState<AgentsDict>({});
  const [seasons, setSeasons] = useState<SeasonsDict>({});
  const [selectedSeason, setSelectedSeason] = useState<string>("");
  const [selectedSponsorId, setSelectedSponsorId] = useState<string>("");
  const [selectedAgentId, setSelectedAgentId] = useState<string>("");
  const [adminSponsorId, setAdminSponsorId] = useState<string>("");
  const [adminAgentId, setAdminAgentId] = useState<string>("");

  const [workerDistribution, setWorkerDistribution] = useState<number[]>([]);

  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<string>("createdAt");

  const [stats, setStats] = useState<Record<string, ApplicationStats>>({});

  const [filteredAgents, setFilteredAgents] = useState<AgentsDict>({});

  const { currentUser } = useContext(AuthContext)!;

  const headCells: HeadCells = [
    {
      id: "createdAt",
      numeric: false,
      disablePadding: false,
      label: "Date",
      sortable: true,
      width: "120px",
    },
    {
      id: "totalCount",
      numeric: false,
      disablePadding: false,
      label: "Total",
      sortable: false,
      width: "100px",
    },
  ];

  APPLICATION_STATUS.forEach((status) => {
    headCells.push({
      id: status.key,
      numeric: true,
      disablePadding: false,
      label: status.label,
      sortable: false,
      width: "100px",
    });
  });

  const fetchStats = async () => {
    if (!selectedSponsorId || !selectedSeason) return;

    const statsDict = await loadApplicationStats(
      selectedSponsorId,
      selectedAgentId,
      selectedSeason
    );

    if (selectedAgentId === "all_agents") {
      const aggregatedStats: Record<string, ApplicationStats> = {};
      Object.values(statsDict).forEach((stat) => {
        const date = stat.createdAt.toString();
        if (!aggregatedStats[date]) {
          aggregatedStats[date] = { ...stat, statusCounts: { ...stat.statusCounts } };
        } else {
          Object.entries(stat.statusCounts).forEach(([key, value]) => {
            aggregatedStats[date].statusCounts[key] =
              (aggregatedStats[date].statusCounts[key] || 0) + value;
          });
        }
      });
      setStats(aggregatedStats);
    } else {
      setStats(statsDict);
    }
  };

  useEffect(() => {
    const fetchSponsors = async () => {
      const sponsorsDict: SponsorsDict = await loadSponsors(currentUser?.appUser);

      if (sponsorsDict) {
        setSponsors(sponsorsDict);
      } else {
        setSponsors({});
      }
    };

    const fetchAgents = async () => {
      const agentsDict: AgentsDict = await loadAgents(currentUser?.appUser);

      if (agentsDict) {
        setAgents(agentsDict);
      } else {
        setAgents({});
      }
    };

    const fetchSeasons = async () => {
      const seasonsDict = await loadSeasons(currentUser?.appUser);
      const year = new Date().getFullYear();
      let seasonId = Object.entries(seasonsDict).find(([id, season]) => {
        return new Date(season.start_date).getFullYear() === year;
      })?.[0];

      if (!seasonId) seasonId = "";

      setSeasons(seasonsDict);
      if (seasonsDict) setSelectedSeason(seasonId);
    };

    const sponsorId = currentUser?.appUser?.sponsorId || "";
    setAdminSponsorId(sponsorId);

    const agentId = currentUser?.appUser?.agentId || "";
    setAdminAgentId(agentId);

    fetchSponsors();
    fetchAgents();
    fetchSeasons();
  }, [currentUser]);

  useEffect(() => {
    if (selectedSponsorId) {
      const filtered = Object.fromEntries(
        Object.entries(agents).filter(([_, agent]) => agent.sponsorId === selectedSponsorId)
      );
      setFilteredAgents(filtered);
    } else {
      setFilteredAgents(agents);
    }
  }, [selectedSponsorId, agents]);

  const handleRefreshData = () => {
    fetchStats();
  };

  const handleShowWorkerDistribution = async () => {
    const filter: Filter = {};

    if (selectedSeason) {
      filter.seasonId = {
        operator: "==",
        value: selectedSeason,
      };
    }

    if (selectedSponsorId) {
      filter.sponsorId = {
        operator: "==",
        value: selectedSponsorId,
      };
    }

    if (selectedAgentId && selectedAgentId !== "all_agents") {
      filter.agentId = {
        operator: "==",
        value: selectedAgentId,
      };
    }

    const workOrders = await loadWorkOrders(currentUser?.appUser, false, filter);

    console.log(`Loaded ${Object.keys(workOrders).length} work orders`);

    const newWorkerDistribution: number[] = Array(12).fill(0);
    await Promise.all(
      Object.entries(workOrders).map(async ([workOrderId, workOrder]) => {
        const applications = await loadApplications(currentUser?.appUser, {
          workOrderId: workOrderId,
        });
        const month = new Date(workOrder.start_date).getMonth();
        newWorkerDistribution[month] += Object.keys(applications).length;
      })
    );
    setWorkerDistribution(newWorkerDistribution);

    let distributionString = "";
    for (let i = 0; i < 12; i++) {
      distributionString += `${workerDistribution[i]}, `;
    }
    console.log(distributionString);
  };

  const handleExport = () => {
    console.log("Exporting applications");
  };

  const onInputComboChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const key = event.target.name;
    const value = event.target.value;
    if (key === "seasonId") {
      setSelectedSeason(value);
    } else if (key === "sponsorId") {
      setSelectedSponsorId(value);
    } else if (key === "agentId") {
      setSelectedAgentId(value);
    } else return;
  };

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const getTotal = (stat: ApplicationStats) => {
    return Object.values(stat.statusCounts).reduce((acc, curr) => Number(acc) + Number(curr), 0);
  };

  const sortedSeasons = useMemo(
    () => Object.entries(seasons).sort(([, a], [, b]) => a.name.localeCompare(b.name)),
    [seasons]
  );

  const visibleRows = useMemo(() => {
    return Object.entries(stats)
      .map(([statId, stat]) => [statId, stat] as [string, ApplicationStats])
      .sort((a, b) => {
        if (orderBy === "createdAt") {
          return order === "asc"
            ? new Date(a[1].createdAt).getTime() - new Date(b[1].createdAt).getTime()
            : new Date(b[1].createdAt).getTime() - new Date(a[1].createdAt).getTime();
        }
        return 0;
      });
  }, [stats, order, orderBy]);

  return (
    <TitledPage title="Applications Stats">
      <>
        <Box
          id="applications-stats-page"
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: "100vh",
          }}
        >
          <Box display={"flex"} sx={{ gap: 2 }}>
            <Tooltip title="Export applications to CSV">
              <span>
                <IconButton
                  color="primary"
                  edge={"end"}
                  sx={{ marginTop: "8px", marginBottom: "4px" }}
                  onClick={handleExport}
                >
                  <SaveIcon />
                </IconButton>
              </span>
            </Tooltip>
            {/* ----------------- Season ---------------- */}
            <TextField
              name={"seasonId"}
              id={"seasonId"}
              label={"Season"}
              select
              onChange={onInputComboChange}
              value={selectedSeason}
              margin={"dense"}
              size="small"
              sx={{ width: "150px" }}
            >
              <MenuItem value="no_season" key="no_season">
                <Typography fontStyle={"italic"}>no season</Typography>
              </MenuItem>
              {sortedSeasons.map(([seasonId, season]) => {
                return (
                  <MenuItem value={seasonId} key={seasonId}>
                    {season.name}
                  </MenuItem>
                );
              })}
            </TextField>

            {/* ----------- Sponsor ----------- */}
            {!adminSponsorId && (
              <TextField
                name={"sponsorId"}
                label={"Sponsor"}
                select
                onChange={onInputComboChange}
                value={selectedSponsorId}
                required
                size="small"
                margin={"dense"}
                sx={{ width: "250px" }}
              >
                <MenuItem value="" key="no_sponsor">
                  <Typography fontStyle={"italic"}>no sponsor selected</Typography>
                </MenuItem>
                {Object.entries(sponsors).map(([sponsorId, sponsor]) => {
                  return (
                    <MenuItem value={sponsorId} key={sponsorId}>
                      {sponsor.name}
                    </MenuItem>
                  );
                })}
              </TextField>
            )}

            {/* ----------- Agent ----------- */}
            {!adminAgentId && (
              <TextField
                name={"agentId"}
                label={"Agent"}
                select
                onChange={onInputComboChange}
                value={selectedAgentId}
                size="small"
                margin={"dense"}
                sx={{ width: "450px" }}
              >
                <MenuItem value="" key="no_agent">
                  <Typography fontStyle={"italic"}>no agent selected</Typography>
                </MenuItem>
                <MenuItem value="all_agents" key="all_agents">
                  <Typography fontStyle={"italic"}>All Agents</Typography>
                </MenuItem>
                {Object.entries(filteredAgents).map(([agentId, agent], index) => {
                  return (
                    <MenuItem value={agentId} key={agentId}>
                      {agent.company}
                    </MenuItem>
                  );
                })}
              </TextField>
            )}
            {adminAgentId && (
              <Typography variant={"body1"}>Agent: {agents[adminAgentId]?.company}</Typography>
            )}
            <Tooltip title="Refresh">
              <span>
                <IconButton
                  color="primary"
                  edge={"end"}
                  sx={{ marginTop: "8px", marginBottom: "4px" }}
                  disabled={!selectedSponsorId || !selectedSeason}
                  onClick={handleRefreshData}
                >
                  <Refresh />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Worker Distribution">
              <span>
                <IconButton
                  color="primary"
                  edge={"end"}
                  sx={{ marginTop: "8px", marginBottom: "4px" }}
                  disabled={!selectedSponsorId || !selectedSeason}
                  onClick={handleShowWorkerDistribution}
                >
                  <Group />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Box
            id="applications-container"
            sx={{
              display: "flex",
              flexDirection: "column",
              marginTop: "1rem",
              height: "calc(100vh - 250px)",
            }}
          >
            <Box
              id="applications-table-container"
              sx={{ marginBottom: "24px", minHeight: "400px", height: "66%" }}
            >
              <TableVirtuoso
                style={{ height: "100%" }}
                data={visibleRows}
                context={{ selectedId: "", handleSelect: () => {} }}
                components={VirtuosoTableComponents}
                fixedHeaderContent={() => (
                  <EnhancedTableHeadContents
                    numSelected={0}
                    order={order}
                    orderBy={orderBy}
                    onSelectAllClick={() => {}}
                    onRequestSort={handleRequestSort}
                    rowCount={visibleRows.length}
                    selectable={false}
                    headCells={headCells}
                  />
                )}
                itemContent={(index, [statId, stat]) => {
                  return (
                    <>
                      <TableCell scope="row" size="small">
                        {stat.createdAt}
                      </TableCell>
                      <TableCell scope="row" size="small">
                        {getTotal(stat)}
                      </TableCell>
                      {APPLICATION_STATUS.map((status) => {
                        return (
                          <TableCell size="small" key={status.key}>
                            {stat.statusCounts[status.key] || 0}
                          </TableCell>
                        );
                      })}
                    </>
                  );
                }}
              />
            </Box>
          </Box>
        </Box>
      </>
    </TitledPage>
  );
}
